// Window predicates for the MLAP delta-band period cache. They MUST mirror the backend's
// interval semantics exactly, otherwise locally-filtered (narrowed) windows would differ
// from server-loaded ones.
//
// MLAP-FILTER DEC-1: period membership is INCLUSIVE INTERVAL OVERLAP for both panes —
// include iff finish >= periodStart AND start <= periodEnd. Boundary touches are IN, and a
// row spanning the whole window (running right now) is IN. Progress plays no part (DEC-2).
// - Schedule Activities: earlyStartDate <= finish AND earlyFinishDate >= start
//   (see findLookAheadIdsByScenarioIdsAndInterval).
// - Resource Tasks: same rule on the backend's expanded window [startT00:00:00, finishT23:59:59]
//   (see DateDTO.getStartDateTime/getFinishDateTime and findLookAheadRtIdsByPortfolioIdAndIntervalAndFilters).
// All comparisons are lexical on ISO strings ("YYYY-MM-DD" / "YYYY-MM-DDTHH:mm:ss"), which
// orders correctly and avoids timezone drift from Date parsing.

// The ONE period predicate (MLAP-2), on stored ISO strings at the caller's granularity:
// inPeriod(start, finish, P0, P1) := finish >= P0 AND start <= P1. Null dates are excluded
// (and reported by the incomplete-dates notice), never guessed.
export const inPeriod = (start, finish, periodStart, periodEnd) =>
  start != null &&
  finish != null &&
  String(finish) >= periodStart &&
  String(start) <= periodEnd;

export const saRowInWindow = (row, win) => {
  if (!win) return true;
  const sa = row?.scheduleActivity;
  return inPeriod(sa?.earlyStartDate, sa?.earlyFinishDate, win.start, win.finish);
};

export const rtRowInWindow = (row, win) => {
  if (!win) return true;
  const rt = row?.resourceTask;
  return inPeriod(rt?.start, rt?.finish, `${win.start}T00:00:00`, `${win.finish}T23:59:59`);
};

// Coverage math shared by both delta thunks. Windows/coverages are {start, finish} of
// "YYYY-MM-DD" strings.
export const windowInsideCoverage = (win, cov) =>
  win.start >= cov.start && win.finish <= cov.finish;

export const windowsOverlap = (win, cov) =>
  win.start <= cov.finish && win.finish >= cov.start;

// The missing band(s) to fetch when widening `cov` to include `win`.
export const missingBands = (win, cov) => {
  const bands = [];
  if (win.start < cov.start) bands.push({ start: win.start, finish: cov.start });
  if (win.finish > cov.finish)
    bands.push({ start: cov.finish, finish: win.finish });
  return bands;
};

export const mergeCoverage = (win, cov) => ({
  start: win.start < cov.start ? win.start : cov.start,
  finish: win.finish > cov.finish ? win.finish : cov.finish,
});
